import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { CgAttachment } from 'react-icons/cg';
import { FiInfo } from 'react-icons/fi';
import { Send } from 'lucide-react';
import { LoginContext } from '../../Contexts/LoginContext';
import { showToast } from '../utils/ShowToast';

const API = process.env.REACT_APP_Backend_Host_Name;

const OrderChatInput = ({ orderId, onMessageSent }) => {
  const { user } = useContext(LoginContext);
  const token = localStorage.getItem('token');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [chatWarnings, setChatWarnings] = useState([]);
  const [activeWarning, setActiveWarning] = useState(null);

  useEffect(() => {
    if (!token) return;
    const fetchWarnings = async () => {
      try {
        const res = await axios.get(`${API}/api/v1/restrictions`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success && res.data.restriction?.chatWarnings) {
          setChatWarnings(res.data.restriction.chatWarnings);
        }
      } catch { }
    };
    fetchWarnings();
  }, [token]);

  const checkChatWarnings = (text) => {
    if (!text || !chatWarnings.length) return null;
    const lower = text.toLowerCase();
    for (const w of chatWarnings) {
      for (const word of w.words) {
        if (lower.includes(word.toLowerCase())) return w;
      }
    }
    return null;
  };

  const sendMessage = async () => {
    if ((!message.trim() && !file) || sendLoading) return;
    const warning = checkChatWarnings(message);
    if (warning) {
      setActiveWarning(warning);
      return;
    }
    setSendLoading(true);
    const formData = new FormData();
    formData.append('text', message);
    if (file) formData.append('file', file);
    try {
      const res = await axios.post(`${API}/api/v1/orders/${orderId}/chat`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        setMessage('');
        setFile(null);
        setActiveWarning(null);
        onMessageSent?.(res.data.message);
      }
    } catch (err) {
      showToast(err?.response?.data?.error || 'Failed to send', 'error');
    } finally {
      setSendLoading(false);
    }
  };

  const handleFileChange = (e) => setFile(e.target.files[0] || null);
  const removeFile = () => setFile(null);

  if (!orderId || !user) return null;

  return (
    <div className="orderChatInputBar">
      {activeWarning && (
        <div className="chatWarningBanner orderChatWarning">
          <FiInfo className="chatWarningIcon" />
          <span>{activeWarning.message}</span>
          <button className="chatWarningClose" onClick={() => setActiveWarning(null)}>&times;</button>
        </div>
      )}
      {file && (
        <div className="orderChatFilePreview">
          <span>{file.name}</span>
          <button type="button" onClick={removeFile} className="orderChatRemoveFile">&times;</button>
        </div>
      )}
      <div className="orderChatInputRow">
        <input
          type="text"
          value={message}
          onChange={e => {
            setMessage(e.target.value);
            setActiveWarning(checkChatWarnings(e.target.value));
          }}
          placeholder="Type a message..."
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendMessage(); } }}
          disabled={sendLoading}
        />
        <label className="orderChatAttachBtn">
          <CgAttachment size={20} />
          <input type="file" onChange={handleFileChange} className="orderChatFileInput" aria-label="Attach file" />
        </label>
        <button className="orderChatSendBtn" onClick={sendMessage} disabled={sendLoading}>
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

export default OrderChatInput;
